import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import Cookies from 'universal-cookie';
import { Link } from 'react-router-dom';
import {
  Alert, Button, CircularProgress, LinearProgress, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import Box from 'elements/Box';
import { ProjectConfig } from 'Global';
import AniversariosEditorialImporter from './AniversariosEditorialImporter';

type Plan = {
  success:boolean; pending:number; counts:Record<string,number>;
  latin_provider_configured:boolean; existing_json_protected:number;
};
type Batch = {
  run_id?:string; mode?:string;
  state:'idle'|'queued'|'running'|'stopping'|'stopped'|'blocked'|'completed'|'partial'|'interrupted';
  total?:number; attempted?:number; saved?:number; skipped?:number; failed?:number;
  remaining?:number|null; current?:string; message?:string; errors?:string[];
  updated_at?:number;
};
const lang: Record<string,string> = {
  en:'Inglés',it:'Italiano',pt:'Portugués',fr:'Francés',ca:'Catalán',la:'Latín',
};
const modern = ['en','it','pt','fr','ca'];
const active = new Set(['queued','running','stopping']);
const terminal = new Set(['completed','partial','blocked','interrupted','stopped']);

function errorMessage(err:any):string {
  const data = err?.response?.data;
  return String(
    (typeof data === 'string' ? data : data?.error || data?.message) ||
      err?.message || 'No fue posible completar la solicitud.'
  ).slice(0,550);
}

export default function AniversariosTranslateMissing() {
  const [plan,setPlan] = useState<Plan|null>(null);
  const [batch,setBatch] = useState<Batch>({state:'idle'});
  const [loading,setLoading] = useState(true);
  const [busy,setBusy] = useState(false);
  const [error,setError] = useState('');
  const lastTerminal = useRef('');
  const token = () => 'Bearer ' + (new Cookies().get('ltkn') || '');

  const refreshPlan = useCallback(async () => {
    try {
      const response = await axios.get(ProjectConfig.api_url,{
        params:{request:'aniversarios_biography_translation_plan'},
        headers:{Authorization:token()},timeout:45000,
      });
      if (response.data?.success !== true || !response.data?.counts) {
        throw new Error(response.data?.error || 'La auditoría no devolvió resultados.');
      }
      setPlan(response.data as Plan);
    } catch(err:any) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  },[]);

  const refreshStatus = useCallback(async () => {
    try {
      const response = await axios.get(ProjectConfig.api_url,{
        params:{request:'aniversarios_biography_batch_status'},
        headers:{Authorization:token()},timeout:30000,
      });
      if (response.data?.success !== true || !response.data?.batch) {
        throw new Error(response.data?.error || 'Estado no disponible.');
      }
      const next:Batch = response.data.batch;
      setBatch(next);
      const completedKey = (next.run_id || '') + ':' + next.state;
      if (terminal.has(next.state) && completedKey !== lastTerminal.current) {
        lastTerminal.current = completedKey;
        void refreshPlan();
      }
    } catch(err:any) {
      setError('No fue posible consultar el estado del servidor: ' + errorMessage(err));
    }
  },[refreshPlan]);

  useEffect(() => {
    void refreshPlan();
    void refreshStatus();
    const poll = window.setInterval(() => {void refreshStatus();},10000);
    return () => window.clearInterval(poll);
  },[refreshPlan,refreshStatus]);

  const modernCount=modern.reduce((sum,code)=>sum+(plan?.counts?.[code]||0),0);

  const start = async () => {
    if (busy || loading || modernCount === 0 || active.has(batch.state)) return;
    setBusy(true);setError('');
    try {
      const values = new FormData();
      values.append('mode','modern');
      const response = await axios.post(ProjectConfig.api_url, values, {
        params:{request:'aniversarios_biography_batch_start'},
        headers:{Authorization:token()},timeout:30000,
      });
      if (response.data?.success !== true) {
        throw new Error(response.data?.error || 'No fue posible iniciar el lote.');
      }
      setBatch(response.data.batch);
      void refreshStatus();
    } catch(err:any) {
      setError(errorMessage(err));
    } finally {setBusy(false);}
  };

  const stop = async () => {
    if (busy || !active.has(batch.state)) return;
    setBusy(true);setError('');
    try {
      const response = await axios.post(ProjectConfig.api_url,new FormData(),{
        params:{request:'aniversarios_biography_batch_stop'},
        headers:{Authorization:token()},timeout:25000,
      });
      if(response.data?.success!==true)throw new Error(response.data?.error||'No fue posible detener.');
      setBatch(response.data.batch);
    } catch(err:any) {
      setError(errorMessage(err));
    } finally {setBusy(false);}
  };

  const total=batch.total||0;
  const attempted=batch.attempted||0;
  const percentage=total?Math.min(100,Math.round(attempted*100/total)):0;
  const hasActivity=active.has(batch.state);
  return (
    <div id="ProductList">
      <div className="buttons_top">
        <Button component={Link} to="/admin/aniversarios">Volver a Aniversarios</Button>
        <Button component={Link} to="/admin/aniversarios/auditoria-biografias">Auditoría</Button>
        <Button variant="outlined" disabled={loading||busy}
          onClick={()=>{void refreshPlan();void refreshStatus();}}>Actualizar estado</Button>
      </div>
      <Box id="aniversarios_translate_missing" title="Traducciones de biografías"
        subtitle="Proceso independiente del navegador · sin sobrescribir textos">
        <Stack spacing={2}>
          <Alert severity="info">
            El servidor hace las traducciones. Puedes cerrar esta página y volver después:
            el proceso no depende de tener el navegador abierto. Solo se escriben campos
            realmente vacíos, y cada traducción se valida en la base de datos.
          </Alert>
          {error && <Alert severity="error">{error}</Alert>}
          {loading && <CircularProgress/>}
          {plan && <>
            <Typography><strong>Idiomas locales pendientes:</strong> {modernCount}.
              {' '}<strong>Latín pendiente:</strong> {plan.counts?.la||0} (separado, no se iniciará automáticamente).</Typography>
            <TableContainer><Table size="small"><TableHead><TableRow>
              <TableCell>Idioma</TableCell><TableCell align="right">Pendientes reales</TableCell>
            </TableRow></TableHead><TableBody>
              {Object.entries(lang).map(([code,title])=><TableRow key={code}>
                <TableCell>{title}</TableCell>
                <TableCell align="right">{plan.counts?.[code]||0}</TableCell>
              </TableRow>)}
            </TableBody></Table></TableContainer>
            {plan.existing_json_protected>0 &&
              <Alert severity="warning">
                {plan.existing_json_protected} campos contienen datos estructurados protegidos.
                No serán sobrescritos.
              </Alert>}
          </>}
          {hasActivity && <Alert severity="info">
            El proceso está {batch.state==='queued'?'en cola':
              batch.state==='stopping'?'deteniéndose':'trabajando en el servidor'}.
            Puedes salir de esta página y volver cuando quieras.
          </Alert>}
          {batch.message && <Typography><strong>Estado:</strong> {batch.message}</Typography>}
          {(total>0 || attempted>0) && <Stack spacing={1}>
            <Typography>
              {attempted} / {total} procesadas;
              {' '}{batch.saved||0} traducciones nuevas guardadas;
              {' '}{batch.skipped||0} existentes protegidas;
              {' '}{batch.failed||0} fallidas.
            </Typography>
            <LinearProgress variant="determinate" value={percentage}/>
            {batch.current && <Typography variant="body2">
              Ahora: {batch.current}
            </Typography>}
          </Stack>}
          {batch.state==='completed' && <Alert severity="success">
            Cinco idiomas completos: el servidor verificó que no quedan campos elegibles vacíos.
            El latín continúa como tarea separada.
          </Alert>}
          {['blocked','partial','interrupted'].includes(batch.state) &&
            <Alert severity="warning">
              El proceso se detuvo o finalizó con pendientes. Los textos guardados siguen intactos;
              consulta los errores antes de reintentar. Puedes reanudar sin sobrescribir lo ya guardado.
            </Alert>}
          {Array.isArray(batch.errors)&&batch.errors.length>0 &&
            <Alert severity="warning">
              Últimos errores: {batch.errors.join(' | ')}
            </Alert>}
          <Stack direction="row" spacing={2}>
            {!hasActivity && modernCount>0 &&
              <Button variant="contained" disabled={busy||loading}
                onClick={()=>void start()}>
                {busy?'Iniciando…':'Completar pendientes en cinco idiomas (gratis)'}
              </Button>}
            {hasActivity && <Button variant="outlined" disabled={busy}
              onClick={()=>void stop()}>Detener de forma segura</Button>}
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Esta traducción emplea LibreTranslate local, sin API de pago. El latín no se procesa
            porque el traductor local no lo admite. Las traducciones existentes y las biografías
            españolas permanecen intactas.
          </Typography>
        </Stack>
      </Box>
      <AniversariosEditorialImporter />
    </div>
  );
}
